import { Form } from 'formik'
import { Heading, Stack, useResponsiveValue, useToast } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { useQuery } from '@tanstack/react-query'
import { useSponsoredListingCreditInformation } from '@/hooks/use-block-and-pay'
import { usePaymentsQuery } from '@/hooks/use-payments'
import { getDryRun } from '@/hooks/use-dry-run'
import useProfile from '@/hooks/use-profile'
import SummaryOverview from './summary-overview'
import SummaryPurchase from './summary-purchase'
import SummaryPayment from './summary-payment'
import FormWrapper from '../form-wrapper'
import SponsoredListing from '../components/sponsored-listing'
import FormFooter from '../components/footer'
import { useBoostingPackages } from '../hooks/use-boosting-packages'
import { Listing } from '../types'
import { PackageNameToDurationMap } from '.'

interface Props {
  data: Listing
  enableValidation: () => void
}

export const SummaryStateDriver = ({ data, enableValidation }: Props) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext<any>()
  const { t } = useTranslation()
  const toast = useToast()
  const profile = useProfile()
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const { packages, sevenDaysPackage, fourteenDaysPackage, thiryDaysPackage } =
    useBoostingPackages()

  const usage = useSponsoredListingCreditInformation()

  const hasEnoughCredits = useMemo(() => {
    const creditCost =
      PackageNameToDurationMap[values.duration as keyof typeof PackageNameToDurationMap]
    if (creditCost) {
      setFieldValue('hasEnoughCredits', usage.availableCredits >= creditCost)
      return usage.availableCredits >= creditCost
    }
    return true
  }, [values.duration, setFieldValue, usage.availableCredits])

  useEffect(() => {
    // from earlier step
    if (localStorage.getItem('selectedBoostingPackage')) {
      setFieldValue('duration', localStorage.getItem('selectedBoostingPackage'))
      return
    }
  }, [packages, setFieldValue])

  const paymentQuery = usePaymentsQuery()

  useEffect(() => {
    if (!paymentQuery.isLoading) {
      if (!isEmpty(paymentQuery?.data?.body)) {
        setFieldValue('payment_method_id', paymentQuery?.data?.body?.[0]?.payment_method_id)
      } else {
        enableValidation() // of the "New payment method" form
      }
    }
  }, [enableValidation, paymentQuery.isLoading, paymentQuery.data, setFieldValue])

  const dryRunState =
    values.state || paymentQuery?.data?.body?.[0]?.state || profile?.company_info?.state || 'BC'
  const dryRunCountry = (values.country || paymentQuery?.data?.body?.[0]?.country) ?? 'CA'
  const dryRunQuery = useQuery({
    queryKey: [
      'dryRun',
      packages,
      dryRunState,
      dryRunCountry,
      values.discount_code,
      values.duration
    ],
    enabled:
      !hasEnoughCredits &&
      !isEmpty(values.duration) &&
      !isEmpty(packages) &&
      !isEmpty(dryRunState) &&
      !isEmpty(dryRunCountry),
    queryFn: async () => {
      const product = packages.find((pkg: any) => pkg.plan_name === values.duration)
      const resp = await getDryRun({
        items: [
          {
            priceId: product?.price_id,
            quantity: 1,
            useCredits: false
          }
        ],
        metadata: {
          plan_name: product?.plan_name
        },
        couponCode: values.discount_code,
        state: dryRunState,
        country_code: dryRunCountry
      })

      if (!resp?.response.ok) {
        toast({ message: 'Unable to fetch listing cost', tone: 'critical' })
        return Promise.reject('Unable to fetch listing cost')
      }
      return resp?.body
    },
    select: body => {
      if (!body) return null
      return {
        lineItem: body.items?.[0],
        metadata: body?.metadata,
        tax: body?.tax,
        total: body?.total,
        total_excluding_discount: body?.total_excluding_discount,
        total_excluding_tax: body?.total_excluding_tax
      }
    },
    retry: false,
    refetchOnMount: false,
    staleTime: 5 * 60000
  })

  return (
    <FormWrapper>
      <Form>
        <Stack align="left" space={{ mobile: 'gutter', desktop: 'large' }}>
          {!isMobile && (
            <Heading level="4" component="h2">
              {t('Listing Summary')}
            </Heading>
          )}
          <SummaryOverview data={data} />
          <SponsoredListing
            isLoading={
              sevenDaysPackage.isLoading ||
              fourteenDaysPackage.isLoading ||
              thiryDaysPackage.isLoading ||
              usage.isLoading
            }
            packages={packages}
            availableCredits={usage.availableCredits}
            onSelectPackage={pkg => {
              setFieldValue('duration', pkg?.plan_name ?? '')
            }}
          />
          {!isEmpty(values.duration) && !hasEnoughCredits && (
            <>
              <SummaryPurchase
                isLoading={
                  sevenDaysPackage.isLoading ||
                  fourteenDaysPackage.isLoading ||
                  thiryDaysPackage.isLoading ||
                  dryRunQuery.isLoading
                }
                data={dryRunQuery.data}
                isError={dryRunQuery.isError}
              />
              <SummaryPayment enableValidation={enableValidation} />
            </>
          )}
          <FormFooter
            isLoading={
              sevenDaysPackage.isLoading ||
              fourteenDaysPackage.isLoading ||
              thiryDaysPackage.isLoading ||
              usage.isLoading ||
              dryRunQuery.isLoading
            }
            isSubmitting={isSubmitting}
            step="listing-summary"
          />
        </Stack>
      </Form>
    </FormWrapper>
  )
}
